import React, { useState } from 'react';
import PropTypes from 'prop-types';
import * as R from 'ramda';
import { add, isBefore, startOfToday } from 'date-fns';
import { useParams } from 'react-router-dom';
import MenuItem from '@mui/material/MenuItem';
import Box from '@mui/material/Box';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import { useUserRolePermissions } from 'hooks';
import { useViewTrailerDetailsByLocationQuery } from '../trailerStatusEndpoints';
import TrailerCard, { TrailerCardSkeleton } from './TrailerCard';
import ViewTrailerDrawer from './ViewTrailerDrawer';
import AddTrailersWizard from './add-trailers-to-location-wizard/AddTrailersWizard';

const addBlockStyles = {
  background: 'white',
  borderRadius: '2px',
  boxShadow: '4px 8px 24px 1px rgba(0, 0, 0, 0.08)',
  p: 2,
};
const addButtonBlockStyles = {
  alignItems: 'center',
  border: '1px dashed',
  borderColor: 'text.secondary',
  display: 'flex',
  height: '100%',
  width: '277px',
};
const addButtonStyles = {
  alignItems: 'center',
  border: '1px solid',
  borderColor: 'secondary.medium',
  borderRadius: 18,
  color: 'secondary.main',
  display: 'flex',
  margin: '0 auto',
  px: 2,
  py: '6px',
};

export const AddItemButton = ({ itemName, onAdd }) => (
  <MenuItem onClick={onAdd} sx={addBlockStyles}>
    <Box sx={addButtonBlockStyles}>
      <Box sx={addButtonStyles}>
        <AddRoundedIcon sx={{ mr: '12px' }} />
        Add {itemName}
      </Box>
    </Box>
  </MenuItem>
);

AddItemButton.propTypes = {
  itemName: PropTypes.string.isRequired,
  onAdd: PropTypes.func.isRequired,
};

const TrailerCardList = ({
  setEditTrailerId,
  expFilterValue,
  vtcFilterValue,
  statusFilterValue,
}) => {
  const [trailerIdView, setTrailerIdView] = useState('');
  const { viewById } = useParams();
  const { isDispatchingManage } = useUserRolePermissions();

  const locationId = parseInt(viewById);
  const params = { locationId };
  const {
    data = [],
    isLoading,
    isSuccess,
  } = useViewTrailerDetailsByLocationQuery({ params }, { skip: R.isNil(viewById) });

  const vtcFilter = ({ vaporTightnessCode }) =>
    vaporTightnessCode.toUpperCase().includes(vtcFilterValue.toUpperCase());

  // prettier-ignore
  const expFilter = ({ expirationDate }) => {
    const date = new Date(expirationDate);
    if (expFilterValue === 'Valid') return isBefore(startOfToday(), date);
    if (expFilterValue === 'Expired') return isBefore(date, startOfToday());
    if (expFilterValue === '30 Days') return (
      isBefore(startOfToday(), date) && 
      isBefore(date, add(startOfToday(), { days: 30 }))
    );
    return true;
  };

  const statusFilter = ({ status }) => {
    if (statusFilterValue === 'Any' || R.isNil(statusFilterValue)) {
      return true;
    }

    return status.toUpperCase() === statusFilterValue.toUpperCase();
  };

  const filteredTrailers = data.filter(expFilter).filter(vtcFilter).filter(statusFilter);

  const [addTrailerOpened, openAddTrailer] = useState(false);
  const handleAddTrailerOpen = R.compose(openAddTrailer, R.T);
  const handleAddTrailerClose = R.compose(openAddTrailer, R.F);

  if (isLoading)
    return (
      <>
        <TrailerCardSkeleton />
        <TrailerCardSkeleton />
        <TrailerCardSkeleton />
      </>
    );

  const handleViewTrailerClose = R.compose(setTrailerIdView, R.always(''));

  return (
    <>
      {isSuccess && isDispatchingManage && (
        <AddItemButton itemName="Trailer" onAdd={handleAddTrailerOpen} />
      )}

      {filteredTrailers.map(trailer => (
        <TrailerCard
          key={trailer.trailerId}
          onEditTrailer={setEditTrailerId}
          onViewTrailer={setTrailerIdView}
          trailer={trailer}
        />
      ))}

      {trailerIdView && (
        <ViewTrailerDrawer
          onClose={handleViewTrailerClose}
          onEdit={setEditTrailerId}
          trailerId={trailerIdView}
        />
      )}
      {addTrailerOpened && (
        <AddTrailersWizard onClose={handleAddTrailerClose} openDrawer={addTrailerOpened} />
      )}
    </>
  );
};

TrailerCardList.propTypes = {
  expFilterValue: PropTypes.string.isRequired,
  setEditTrailerId: PropTypes.func.isRequired,
  statusFilterValue: PropTypes.string.isRequired,
  vtcFilterValue: PropTypes.string.isRequired,
};

export default TrailerCardList;
